import React, { useEffect } from "react";
import { UseFormWatch, useForm, SubmitHandler } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { Divider } from "@mui/material";

import { Button } from "@/components/form/Button/Button";
import { ConditionalRender } from "@/lib/conditional-render/ConditionalRender";
import { WithClassName } from "@/models/utils.models";
import { useSearchParamsContextV2 } from "@/shared/search-params-context";
import { clsxm } from "@/utils/tailwindMerge";

import {
  searchInputsSchemaDefaults,
  searchSliderMinMaxValues,
} from "../constants";
import { useSearchPageContext } from "../context/searchPage.context";
import { SearchInputsSchema } from "../models";
import { LabelWithSwitch } from "./LabelWithSwitch";
import { MoreFilters } from "./MoreFilters";
import { SearchLadyIdInput } from "./SearchLadyIdInput";
import { SearchRangeSlider } from "./SearchRangeSlider";

type PropTypes = WithClassName & {
  closeDrawer?: () => void;
};

export const SearchFilters: React.FC<PropTypes> = ({
  className,
  closeDrawer,
}) => {
  const { submitToSearchParamsContext, formValues } = useSearchPageContext();
  const { urlSearchParams } = useSearchParamsContextV2(SearchInputsSchema);
  const { handleSubmit, control, watch, reset, setValue } =
    useForm<SearchInputsSchema>({
      resolver: zodResolver(SearchInputsSchema),
      defaultValues: searchInputsSchemaDefaults,
    });

  const isShowResetButton =
    !!urlSearchParams && Object.keys(urlSearchParams).length > 0;

  const { ageMinMax, heightMinMax, weightMinMax } = searchSliderMinMaxValues;

  const onSubmit: SubmitHandler<SearchInputsSchema> = (formParams) => {
    closeDrawer && closeDrawer();
    submitToSearchParamsContext(formParams);
  };

  useEffect(() => {
    if (!!formValues) {
      reset(formValues);
    } else {
      reset(searchInputsSchemaDefaults);
    }
  }, [formValues, reset]);

  return (
    <form
      className={clsxm(
        "flex flex-col gap-4 w-[240px] shrink-0 relative",
        className,
      )}
      onSubmit={handleSubmit(onSubmit)}
    >
      <SearchLadyIdInput control={control} />
      <div className="flex flex-col gap-2">
        <SearchRangeSlider
          control={control}
          labelPrefix="Age"
          unitLabel="years"
          minMaxValues={ageMinMax}
          controllerName="age"
          pinkVersion
        />
        <SearchRangeSlider
          control={control}
          labelPrefix="Height"
          unitLabel="cm"
          minMaxValues={heightMinMax}
          controllerName="height"
          minMaxLabels
          pinkVersion
        />
        <SearchRangeSlider
          control={control}
          labelPrefix="Weight"
          unitLabel="kg"
          minMaxValues={weightMinMax}
          controllerName="weight"
          minMaxLabels
          pinkVersion
        />
      </div>
      <Divider />
      <div className="flex flex-col gap-0">
        <LabelWithSwitch
          name="hasIntroVideo"
          label="Has Intro video"
          control={control}
          pinkVersion
        />
        <LabelWithSwitch
          name="online"
          label="Lady is online"
          control={control}
          pinkVersion
        />
        <LabelWithSwitch
          name="hasVideo"
          label="Has video"
          control={control}
          pinkVersion
        />
        <LabelWithSwitch
          name="ladiesWithCam"
          label="Has camera On"
          control={control}
          pinkVersion
        />
        <LabelWithSwitch
          name="birthdaySoon"
          label="Birthday soon"
          control={control}
          pinkVersion
        />
        <LabelWithSwitch
          name="newest"
          label="New profile"
          control={control}
          pinkVersion
        />
        <LabelWithSwitch
          name="topLadies"
          label="Top 1000"
          control={control}
          pinkVersion
        />
      </div>
      <Divider />
      <MoreFilters
        setValue={setValue}
        control={control}
        watch={watch as unknown as UseFormWatch<SearchInputsSchema>}
      />
      <div
        className={clsxm(
          "sticky bottom-0 -mt-4 py-4 -mx-2 px-2 z-[1]",
          "bg-gradient-to-t from-white from-80% to-white/0 to-95%",
          "flex gap-2",
        )}
      >
        <ConditionalRender on={isShowResetButton}>
          <Button
            size="large"
            variant="secondary"
            className="w-full"
            onClick={() => onSubmit({})}
          >
            reset
          </Button>
        </ConditionalRender>
        <Button
          size="large"
          variant="primary"
          onHoverVariant="gradient-flip"
          type="submit"
          className="w-full"
        >
          search
        </Button>
      </div>
    </form>
  );
};
